export type GameMotionSetting = "full" | "reduced" | "minimal";
export type GameParticleDensity = "off" | "low" | "standard" | "high";
export type GameWordContrast = "standard" | "high";
export type GameUpgradeId =
  | "wordBlast"
  | "wordWall"
  | "wordFreeze"
  | "wordRepel"
  | "integrityRepair";

export interface GameAccessibilitySettings {
  motion: GameMotionSetting;
  particleDensity: GameParticleDensity;
  wordContrast: GameWordContrast;
  wordScale: number;
  simplifiedBackground: boolean;
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  typingVolume: number;
}

export interface GameEffectBudget {
  particleLimit: number;
  projectilesPerHit: number;
  trailsEnabled: boolean;
  backgroundAnimationEnabled: boolean;
}

export interface GameCharacterPresentation {
  id: "wizard" | "ape" | "ninja" | "robot" | "dragon";
  name: string;
  archetype: string;
  tagline: string;
  passive: string;
  active: string;
  accent: string;
  artPath: string;
}

export interface GameUpgradeSnapshot {
  wave: number;
  coins: number;
  coreIntegrity: number;
  upgrades: {
    wordBlast: number;
    wordWall: number;
    wordFreeze: number;
  };
  wordRepelCharges: number;
  wordRepelCapacity: number;
}

export interface GameUpgradeChoice {
  id: GameUpgradeId;
  title: string;
  icon: string;
  description: string;
  effect: string;
  currentLevel: number;
  nextLevel: number;
  maxLevel: number;
  cost: number;
  affordable: boolean;
}

export interface GameUpgradeApplication {
  accepted: boolean;
  reason: string;
  rewardSummary: string;
  state: GameUpgradeSnapshot;
}

export const DEFAULT_GAME_ACCESSIBILITY_SETTINGS: GameAccessibilitySettings = {
  motion: "full",
  particleDensity: "standard",
  wordContrast: "standard",
  wordScale: 1,
  simplifiedBackground: false,
  masterVolume: 0.7,
  musicVolume: 0.45,
  effectsVolume: 0.75,
  typingVolume: 0.35,
};

export const GAME_CHARACTER_PRESENTATIONS: GameCharacterPresentation[] = [
  {
    id: "wizard",
    name: "The Arcanist",
    archetype: "Balanced",
    tagline: "Measured power for every kind of run.",
    passive: "Clean streaks intensify arcane charge.",
    active: "Stasis Field",
    accent: "#67e8f9",
    artPath: "/assets/game/champion-wizard.svg",
  },
  {
    id: "ape",
    name: "The Vanguard",
    archetype: "Impact",
    tagline: "Long words land with arena-shifting force.",
    passive: "Long completions strengthen knockback.",
    active: "Ground Break",
    accent: "#fbbf24",
    artPath: "/assets/game/champion-ape.svg",
  },
  {
    id: "ninja",
    name: "The Veil",
    archetype: "Precision",
    tagline: "Clean first attempts turn danger into momentum.",
    passive: "First-attempt accuracy increases danger score.",
    active: "Threat Mark",
    accent: "#c084fc",
    artPath: "/assets/game/champion-ninja.svg",
  },
  {
    id: "robot",
    name: "The Architect",
    archetype: "Control",
    tagline: "Chains and shields become tactical openings.",
    passive: "Chain clears accelerate control effects.",
    active: "Magnetic Fold",
    accent: "#60a5fa",
    artPath: "/assets/game/champion-robot.svg",
  },
  {
    id: "dragon",
    name: "The Emberwing",
    archetype: "Momentum",
    tagline: "Sustained clean play leaves a burning lane.",
    passive: "High momentum adds controlled burn pressure.",
    active: "Ember Line",
    accent: "#fb7185",
    artPath: "/assets/game/champion-dragon.svg",
  },
];

const UPGRADE_DEFINITIONS: Record<
  GameUpgradeId,
  {
    title: string;
    icon: string;
    description: string;
    cost: number;
    maxLevel: number;
  }
> = {
  wordBlast: {
    title: "Word Blast",
    icon: "blast",
    description: "Clean completions clear nearby threats.",
    cost: 5,
    maxLevel: 5,
  },
  wordWall: {
    title: "Word Wall",
    icon: "wall",
    description: "Completions leave a temporary barrier.",
    cost: 5,
    maxLevel: 5,
  },
  wordFreeze: {
    title: "Word Freeze",
    icon: "freeze",
    description: "Periodic stasis gives the whole arena breathing room.",
    cost: 6,
    maxLevel: 5,
  },
  wordRepel: {
    title: "Core Repel",
    icon: "repel",
    description: "Prevent the next breach and push every threat outward.",
    cost: 4,
    maxLevel: 3,
  },
  integrityRepair: {
    title: "Integrity Repair",
    icon: "repair",
    description: "Restore 20 Core integrity immediately.",
    cost: 5,
    maxLevel: 1,
  },
};

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

function numberSetting(
  value: unknown,
  fallback: number,
  minimum = 0,
  maximum = 1,
): number {
  return typeof value === "number" && Number.isFinite(value)
    ? clamp(value, minimum, maximum)
    : fallback;
}

export function normalizeGameAccessibilitySettings(
  value: unknown,
  prefersReducedMotion = false,
): GameAccessibilitySettings {
  const source =
    value && typeof value === "object"
      ? (value as Partial<GameAccessibilitySettings>)
      : {};
  const motion =
    source.motion === "full" ||
    source.motion === "reduced" ||
    source.motion === "minimal"
      ? source.motion
      : prefersReducedMotion
        ? "reduced"
        : DEFAULT_GAME_ACCESSIBILITY_SETTINGS.motion;
  const particleDensity =
    source.particleDensity === "off" ||
    source.particleDensity === "low" ||
    source.particleDensity === "standard" ||
    source.particleDensity === "high"
      ? source.particleDensity
      : motion === "minimal"
        ? "off"
        : motion === "reduced"
          ? "low"
          : DEFAULT_GAME_ACCESSIBILITY_SETTINGS.particleDensity;

  return {
    motion,
    particleDensity,
    wordContrast:
      source.wordContrast === "high" ? "high" : "standard",
    wordScale: numberSetting(source.wordScale, 1, 0.9, 1.35),
    simplifiedBackground: Boolean(source.simplifiedBackground),
    masterVolume: numberSetting(source.masterVolume, 0.7),
    musicVolume: numberSetting(source.musicVolume, 0.45),
    effectsVolume: numberSetting(source.effectsVolume, 0.75),
    typingVolume: numberSetting(source.typingVolume, 0.35),
  };
}

export function deriveGameEffectBudget(
  settings: GameAccessibilitySettings,
  averageFrameMs: number | null = null,
): GameEffectBudget {
  const densityBudget = {
    off: { particles: 0, projectiles: 1 },
    low: { particles: 18, projectiles: 2 },
    standard: { particles: 42, projectiles: 4 },
    high: { particles: 70, projectiles: 6 },
  }[settings.particleDensity];
  const frameStrained = averageFrameMs !== null && averageFrameMs > 24;
  const motionLimited = settings.motion !== "full";

  return {
    particleLimit: frameStrained
      ? Math.min(18, densityBudget.particles)
      : densityBudget.particles,
    projectilesPerHit: frameStrained
      ? 1
      : Math.min(densityBudget.projectiles, motionLimited ? 2 : 6),
    trailsEnabled:
      !frameStrained &&
      !motionLimited &&
      settings.particleDensity !== "off",
    backgroundAnimationEnabled:
      !frameStrained &&
      settings.motion === "full" &&
      !settings.simplifiedBackground,
  };
}

function currentUpgradeLevel(
  id: GameUpgradeId,
  snapshot: GameUpgradeSnapshot,
): number {
  if (id === "wordRepel") {
    return snapshot.wordRepelCharges;
  }
  if (id === "integrityRepair") {
    return snapshot.coreIntegrity >= 100 ? 1 : 0;
  }
  return snapshot.upgrades[id];
}

function effectCopy(
  id: GameUpgradeId,
  nextLevel: number,
  snapshot: GameUpgradeSnapshot,
): string {
  if (id === "wordBlast") {
    return `Blast radius level ${nextLevel}; triggers on a clean completion.`;
  }
  if (id === "wordWall") {
    return `${(1.5 + nextLevel * 0.5).toFixed(1)} second barrier duration.`;
  }
  if (id === "wordFreeze") {
    return `${nextLevel} second freeze with a shorter recharge.`;
  }
  if (id === "wordRepel") {
    return `${Math.min(snapshot.wordRepelCapacity, snapshot.wordRepelCharges + 1)} of ${snapshot.wordRepelCapacity} breach saves stored.`;
  }
  return `${Math.min(100, snapshot.coreIntegrity + 20)}% Core integrity after repair.`;
}

function createUpgradeChoice(
  id: GameUpgradeId,
  snapshot: GameUpgradeSnapshot,
): GameUpgradeChoice {
  const definition = UPGRADE_DEFINITIONS[id];
  const currentLevel = currentUpgradeLevel(id, snapshot);
  const maxLevel =
    id === "wordRepel" ? snapshot.wordRepelCapacity : definition.maxLevel;
  const nextLevel = Math.min(maxLevel, currentLevel + 1);

  return {
    id,
    title: definition.title,
    icon: definition.icon,
    description: definition.description,
    effect: effectCopy(id, nextLevel, snapshot),
    currentLevel,
    nextLevel,
    maxLevel,
    cost: definition.cost,
    affordable: snapshot.coins >= definition.cost && currentLevel < maxLevel,
  };
}

export function getGameUpgradeChoices(
  snapshot: GameUpgradeSnapshot,
): GameUpgradeChoice[] {
  const ordered: GameUpgradeId[] = [
    "wordBlast",
    "wordWall",
    "wordFreeze",
    "wordRepel",
  ];
  const rotation = Math.max(0, Math.floor(snapshot.wave - 1)) % ordered.length;
  const rotated = [...ordered.slice(rotation), ...ordered.slice(0, rotation)];
  const ids =
    snapshot.coreIntegrity <= 75
      ? (["integrityRepair", ...rotated.slice(0, 2)] as GameUpgradeId[])
      : rotated.slice(0, 3);
  return ids.map((id) => createUpgradeChoice(id, snapshot));
}

export function applyGameUpgradeChoice(
  id: GameUpgradeId,
  snapshot: GameUpgradeSnapshot,
): GameUpgradeApplication {
  const choice = createUpgradeChoice(id, snapshot);
  const state: GameUpgradeSnapshot = {
    ...snapshot,
    upgrades: { ...snapshot.upgrades },
  };
  if (!choice.affordable) {
    return {
      accepted: false,
      reason:
        choice.currentLevel >= choice.maxLevel
          ? "This tactical option is already at capacity."
          : `This option needs ${choice.cost} run coins.`,
      rewardSummary: "",
      state,
    };
  }

  state.coins -= choice.cost;
  if (id === "integrityRepair") {
    state.coreIntegrity = Math.min(100, state.coreIntegrity + 20);
  } else if (id === "wordRepel") {
    state.wordRepelCharges = Math.min(
      state.wordRepelCapacity,
      state.wordRepelCharges + 1,
    );
  } else {
    state.upgrades[id] = Math.min(5, state.upgrades[id] + 1);
  }

  return {
    accepted: true,
    reason: "",
    rewardSummary:
      id === "integrityRepair"
        ? `Core restored to ${state.coreIntegrity}%.`
        : `${choice.title} advanced to level ${choice.nextLevel}.`,
    state,
  };
}
